import React, { useRef, useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom';
import { Col, Container, Row } from 'react-bootstrap';

// layout
import AppLayout from '../../layouts/AppLayout/AppLayout';

// components
import { NioSection, NioSubscribeField, NioButton, NioCard, NioIcon, NioSwiper } from '../../components';
import { SwiperSlide } from 'swiper/react';
import ScrollJourney from '../../components/ScrollJourney/ScrollJourney';
import HeroBanner from '../../components/HeroBanner/HeroBanner';

function HoverVideo({ src, poster, stat, statLabel, quote, name, title, link = '/contact-us-solution', startTime = 0 }) {
  const videoRef = useRef(null);
  const [hovered, setHovered] = useState(false);
  const [playing, setPlaying] = useState(false);

  const handleEnter = () => {
    setHovered(true);
    videoRef.current.play();
    setPlaying(true);
  };

  const handleLeave = () => {
    setHovered(false);
    videoRef.current.pause();
    videoRef.current.currentTime = startTime;
    setPlaying(false);
  };

  const togglePlay = (e) => {
    e.stopPropagation();
    if (playing) {
      videoRef.current.pause();
      setPlaying(false);
    } else {
      videoRef.current.play();
      setPlaying(true);
    }
  };

  return (
    <div
      className="border rounded-2 bg-gray-400 overflow-hidden"
      style={{ aspectRatio: '600 / 750', position: 'relative' }}
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
    >
      <video
        ref={videoRef}
        src={src}
        muted
        loop
        playsInline
        preload="metadata"
        onLoadedMetadata={(e) => { e.target.currentTime = startTime || 0.1; }}
        style={{ objectFit: 'cover', width: '100%', height: '100%', display: 'block', objectPosition: '35% center' }}
      />
      <img
        src="images/ws-icon-white.webp"
        alt="Weathersafe logo"
        className="hover-video-logo" loading="lazy"
      />
      <div className="hover-video-overlay">
        {stat && <span className="hover-video-stat">{stat}</span>}
        {statLabel && <span className="hover-video-stat-label">{statLabel}</span>}
        {quote && <p className="hover-video-quote">"{quote}"</p>}
        <div className="hover-video-bottom">
          <div>
            {name && <span className="hover-video-name">{name}</span>}
            {title && <span className="hover-video-title">{title}</span>}
          </div>
          <a href={link} className="hover-video-arrow">&#8599;</a>
        </div>
      </div>
      {hovered && (
        <button
          onClick={togglePlay}
          className="hover-video-btn"
        >
          {playing ? '\u275A\u275A' : '\u25B6'}
        </button>
      )}
    </div>
  );
}

const audienceOptions = ['washing stations', 'farmers', 'traders', 'sustainability managers', 'roasters'];

const audienceData = {
  'farmers': {
    menus: [
      { title: 'A pathway forward', desc: 'Bridging the gaps on your farm — training, mapping and a clear baseline you own.', features: [
        { text: 'GAP77 training every month at your washing station', href: '/about-gap-77' },
        { text: 'Your farm mapped, your trees counted, your baseline set', href: '/methodology#farm-mapping' },
        { text: 'A stronger relationship with your washing station' },
        { text: 'Goals tailored to your farm and what it can do' },
      ], offset: 0 },
      { title: 'On-farm support', desc: 'A scout on your farm every two weeks, with weather, health and yield data straight to you.', features: [
        { text: 'A scout on your farm every two weeks', href: '/blog/every-coffee-farm-every-two-weeks' },
        { text: 'Weather alerts to your phone, three days ahead', href: '/blog/why-coffee-farms-get-wrong-rain-forecast' },
        { text: 'A health score for your farm you can read at a glance', href: '/blog/coffee-yield-forecast-13-years-ground-truth' },
        { text: 'Flowering, pest and cherry numbers logged at every visit' },
      ], offset: 6 },
      { title: 'Triple income', desc: 'Proven practices, weather alerts and farm-level records — yield from 0.3 kg to 4 kg per tree on GAP77 farms.', features: [
        { text: 'Cherry yield up from 0.3 kg to 4 kg per tree', href: '/about-gap-77' },
        { text: 'Banana shade trees that pay you alongside coffee', href: '/blog/gap77-banana-not-eucalyptus' },
        { text: 'A pruning method without a two-year regrowth gap', href: '/about-gap-77' },
        { text: 'Cherry pulp turned into rich soil — input costs cut', href: '/blog/coffee-tera-preta-amazonian-soil' },
      ], offset: 10 },
    ],
    images: ['images/blog/carbon1.webp','images/blog/carbonfarmer.webp','images/blog/cherry.webp','images/blog/colabone.webp','images/blog/process.webp','images/blog/weathercoffee.webp','images/blog/carbon1.webp','images/blog/carbonfarmer.webp','images/blog/cherry.webp','images/blog/colabone.webp','images/blog/process.webp','images/blog/weathercoffee.webp','images/blog/carbon1.webp'],
  },
  'washing stations': {
    menus: [
      { title: 'The hub your farmers rely on', desc: 'Your washing station is more than a processing site — it\'s where training, composting, data and seasonal support come together.', features: [
        { text: 'GAP77 monthly training for every farmer', href: '/faq#how-farmers-are-trained' },
        { text: 'Nursery operations and improved seedling supply', href: '/blog/most-coffee-seedlings-never-produce' },
        { text: 'Cherry pulp turned into compost at the station', href: '/blog/coffee-tera-preta-amazonian-soil' },
        { text: 'Farm mapping, tree counts and per-farmer baselines', href: '/methodology#farm-mapping' },
      ], offset: 0 },
      { title: 'Zone-wide farm oversight', desc: 'Track the health, yield potential and activity of every farm in your catchment, refreshed continuously through the season.', features: [
        { text: 'Red, yellow and green health scores for every farm', href: '/blog/coffee-yield-forecast-13-years-ground-truth' },
        { text: 'Scout visits to every farm, every two weeks', href: '/blog/every-coffee-farm-every-two-weeks' },
        { text: 'Yield forecasts for your full catchment', href: '/blog/flowering-scout-coffee-yield-forecast' },
        { text: 'Pest, disease and weather alerts before issues spread', href: '/faq#pest-disease-outbreaks' },
      ], offset: 6 },
      { title: 'More cherry, less side-selling', desc: 'Better practices across your zone — more consistent cherry, higher grades, and farmer relationships that hold.', features: [
        { text: 'GAP77 implementation tracked across your network', href: '/about-gap-77' },
        { text: 'Higher-graded cherry from the farms you support' },
        { text: 'Stronger farmer relationships, fewer parallel sales' },
      ], offset: 10 },
    ],
    images: ['images/blog/cherry.webp','images/blog/process.webp','images/blog/carbon1.webp','images/blog/carbonfarmer.webp','images/blog/colabone.webp','images/blog/weathercoffee.webp','images/blog/cherry.webp','images/blog/process.webp','images/blog/carbon1.webp','images/blog/carbonfarmer.webp','images/blog/colabone.webp','images/blog/weathercoffee.webp','images/blog/cherry.webp'],
  },
  'traders': {
    menus: [
      { title: 'A supply chain you can see', desc: 'Map your sourcing from washing station to farm — structured visibility across every origin you work with.', features: [
        { text: 'Every farm mapped, every centroid recorded', href: '/methodology#farm-mapping' },
        { text: 'A deforestation-free baseline anchored to the 2020 cut-off', href: '/blog/eudr-due-diligence-coffee' },
        { text: 'Yield and quality records per washing station zone', href: '/blog/farm-level-coffee-records-compliance' },
        { text: 'An audit-ready Origin Enviro+ Report you keep on file', href: '/blog/origin-enviro-plus-report' },
      ], offset: 0 },
      { title: 'Supply chain intelligence', desc: 'Track yield forecasts, farm health and supply risk across your sourcing zones — refreshed continuously, not annually.', features: [
        { text: 'Volume forecasts per origin for hedging and contracts', href: '/blog/coffee-yield-forecast-13-years-ground-truth' },
        { text: 'Deforestation monitoring against your DDS submissions', href: '/blog/eudr-due-diligence-coffee' },
        { text: 'Climate and pest risk tracked across origin regions', href: '/blog/why-coffee-farms-get-wrong-rain-forecast' },
        { text: 'Early warnings before supply disruptions reach you' },
      ], offset: 6 },
      { title: 'Reduce risk, prove compliance', desc: 'Audit-ready evidence retained for the 5-year EUDR window — DDS-linked, ESRS-aligned, defensible at audit.', features: [
        { text: 'Evidence retained for the full 5-year EUDR window', href: '/blog/eudr-due-diligence-coffee' },
        { text: 'Your DDS reference linked back to the original report' },
        { text: 'Transition pathway data ready for CSRD reporting', href: '/blog/coffee-flag-readiness-runway' },
        { text: 'Carbon removals tracked per origin for SBTi FLAG', href: '/blog/sbti-flag-coffee' },
      ], offset: 10 },
    ],
    images: ['images/blog/colabone.webp','images/blog/weathercoffee.webp','images/blog/carbon1.webp','images/blog/cherry.webp','images/blog/process.webp','images/blog/carbonfarmer.webp','images/blog/colabone.webp','images/blog/weathercoffee.webp','images/blog/carbon1.webp','images/blog/cherry.webp','images/blog/process.webp','images/blog/carbonfarmer.webp','images/blog/colabone.webp'],
  },
  'sustainability managers': {
    menus: [
      { title: 'Evidence built from origin', desc: 'Farm-level evidence — geolocation, deforestation-free baselines and 13 years of GAP77 ground truth — that underwrites your claims.', features: [
        { text: 'Every farm geolocated, every origin mapped', href: '/methodology#farm-mapping' },
        { text: 'Deforestation-free baselines anchored to 2020', href: '/blog/eudr-due-diligence-coffee' },
        { text: '13 years of farm-level data underwriting your claims', href: '/about-gap-77' },
        { text: 'A signed Origin Enviro+ Report retained for audit', href: '/blog/origin-enviro-plus-report' },
      ], offset: 0 },
      { title: 'A transition pathway, not a snapshot', desc: 'Continuous monitoring against ESRS E1 / E4 and SBTi FLAG — a yearly trajectory, not an annual audit.', features: [
        { text: 'Continuous monitoring, not annual audit cycles', href: '/blog/every-coffee-farm-every-two-weeks' },
        { text: 'A year-on-year trajectory ready for CSRD reporting', href: '/blog/coffee-flag-readiness-runway' },
        { text: 'Carbon removals and reductions tracked per origin', href: '/blog/sbti-flag-coffee' },
        { text: 'Nature-related risk mapped to source supply zones' },
      ], offset: 6 },
      { title: 'Disclosures buyers and auditors expect', desc: 'Audit-defensible datasets aligned to EUDR, CSRD ESRS and SBTi FLAG — cross-referenced to FAO, IPCC, ESA and Hansen.', features: [
        { text: 'Evidence retained for the 5-year EUDR window', href: '/blog/eudr-due-diligence-coffee' },
        { text: 'Datasets aligned with CSRD, EUDR and SBTi FLAG', href: '/blog/sbti-flag-coffee' },
        { text: 'Verified continuously by satellite and ground data', href: '/blog/farm-level-coffee-records-compliance' },
        { text: 'Cross-referenced to recognised global research sources', href: '/methodology' },
      ], offset: 10 },
    ],
    images: ['images/blog/weathercoffee.webp','images/blog/carbon1.webp','images/blog/carbonfarmer.webp','images/blog/process.webp','images/blog/cherry.webp','images/blog/colabone.webp','images/blog/weathercoffee.webp','images/blog/carbon1.webp','images/blog/carbonfarmer.webp','images/blog/process.webp','images/blog/cherry.webp','images/blog/colabone.webp','images/blog/weathercoffee.webp'],
  },
  'roasters': {
    menus: [
      { title: 'Know where your coffee comes from', desc: 'Direct visibility into your sourcing origins — farms, washing stations and supply relationships, all the way to micro-lot.', features: [
        { text: 'Every farm and washing station mapped to micro-lot', href: '/methodology#farm-mapping' },
        { text: 'Traceability records that go all the way to the roaster', href: '/blog/farm-level-coffee-records-compliance' },
        { text: 'Verified relationships with the farm group, not just the broker' },
        { text: 'An Origin Enviro+ Report you keep for retail and audit', href: '/blog/origin-enviro-plus-report' },
      ], offset: 0 },
      { title: 'Stay close to your origins', desc: 'Track farm health, yield forecasts and climate risk across your origins — every farm scouted every two weeks.', features: [
        { text: 'Cup quality tracked alongside yield and weather', href: '/blog/coffee-yield-forecast-13-years-ground-truth' },
        { text: 'Pest and disease alerts before they hit your cup score', href: '/faq#pest-disease-outbreaks' },
        { text: 'Every farm scouted every two weeks, year-round', href: '/blog/every-coffee-farm-every-two-weeks' },
        { text: 'Climate risk per origin for next-season planning', href: '/blog/why-coffee-farms-get-wrong-rain-forecast' },
      ], offset: 6 },
      { title: 'Tell a defensible story', desc: 'Verified — not certified — sourcing claims that hold up at retail, with your buyers, and at audit.', features: [
        { text: 'Verified, not certified — a continuous evidence record', href: '/about-gap-77' },
        { text: 'An EUDR-ready evidence pack to share with your buyers', href: '/blog/eudr-due-diligence-coffee' },
        { text: 'Sustainability data your customers can actually audit' },
        { text: 'Single-origin and micro-lot stories backed by farm data' },
      ], offset: 10 },
    ],
    images: ['images/blog/process.webp','images/blog/colabone.webp','images/blog/weathercoffee.webp','images/blog/carbon1.webp','images/blog/carbonfarmer.webp','images/blog/cherry.webp','images/blog/process.webp','images/blog/colabone.webp','images/blog/weathercoffee.webp','images/blog/carbon1.webp','images/blog/carbonfarmer.webp','images/blog/cherry.webp','images/blog/process.webp'],
  },
};

function AudienceDropdown({ selected, setSelected }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  React.useEffect(() => {
    function handleClickOutside(e) {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <span ref={ref} style={{ position: 'relative', display: 'inline-flex', flexDirection: 'column' }}>
      <span
        onClick={() => setOpen(o => !o)}
        style={{
          cursor: 'pointer',
          border: '1px solid #555',
          borderRadius: '6px',
          color: '#28a745',
          padding: '2px 10px',
          userSelect: 'none',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '1.5rem',
        }}
      >
        <span style={{ textTransform: 'capitalize' }}>{selected}</span>
        <span style={{ display: 'inline-flex', alignItems: 'center', transition: 'transform 0.3s ease', transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }}>
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </span>
      </span>
      {open && (
        <span style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 'auto',
          minWidth: '100%',
          background: '#fff',
          border: '1px solid #e0e0e0',
          borderRadius: '8px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
          zIndex: 100,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          marginTop: '0.5rem',
        }}>
          {audienceOptions.filter(opt => opt !== selected).map(opt => (
            <span
              key={opt}
              onClick={() => { setSelected(opt); setOpen(false); }}
              style={{
                padding: '0.6rem 1rem',
                fontFamily: "'Inter', sans-serif",
                fontSize: '90%',
                fontWeight: 600,
                letterSpacing: '-0.04em',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                textTransform: 'capitalize',
                color: opt === selected ? '#28a745' : '#000',
                background: 'transparent',
                transition: 'background 0.15s',
              }}
              onMouseEnter={e => e.currentTarget.style.background = '#eaf7ed'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              {opt}
            </span>
          ))}
        </span>
      )}
    </span>
  );
}

// ── Sticky scroll with video swap ────────────────────────────────────────────
function StickyVideoSection({ eyebrowText, heading, subheading, features, reversed = false }) {
  const containerRef = useRef(null);
  const textRefs = useRef([]);
  const videoRefs = useRef([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [panelVisible, setPanelVisible] = useState(false);
  const inter = "'Inter', sans-serif";
  const eyebrowStyle = { display: 'block', fontFamily: inter, fontWeight: 600, fontSize: '0.85rem', letterSpacing: '0.06em', textTransform: 'uppercase', color: '#28a745', marginBottom: '0.75rem' };
  const sectionHeadingStyle = { fontFamily: inter, fontWeight: 700, fontSize: 'clamp(1.8rem, 3vw, 2.6rem)', letterSpacing: '-0.02em', color: '#000', marginBottom: '1rem' };
  const bodyTextStyle = { fontFamily: inter, fontWeight: 400, fontSize: '1.1rem', lineHeight: 1.7, color: '#444' };

  useEffect(() => {
    const checkVisibility = () => {
      const firstText = textRefs.current[0];
      if (!firstText) return;
      const rect = firstText.getBoundingClientRect();
      setPanelVisible(rect.top < window.innerHeight * 0.7);
    };
    window.addEventListener('scroll', checkVisibility, { passive: true });
    checkVisibility();
    return () => window.removeEventListener('scroll', checkVisibility);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const container = containerRef.current;
      if (!container) return;
      const containerRect = container.getBoundingClientRect();
      if (containerRect.bottom < 0 || containerRect.top > window.innerHeight) return;
      let closestIndex = 0;
      let closestDist = Infinity;
      textRefs.current.forEach((el, i) => {
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const center = rect.top + rect.height / 2;
        const viewCenter = window.innerHeight * 0.45;
        const dist = Math.abs(center - viewCenter);
        if (dist < closestDist) { closestDist = dist; closestIndex = i; }
      });
      setActiveIndex(closestIndex);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [features]);

  // Play active video, pause others
  useEffect(() => {
    videoRefs.current.forEach((v, i) => {
      if (!v) return;
      if (i === activeIndex && panelVisible) { v.play().catch(() => {}); }
      else { v.pause(); }
    });
  }, [activeIndex, panelVisible]);

  return (
    <div ref={containerRef}>
      <Container>
        <div style={{ paddingTop: '2rem', marginBottom: '4rem' }}>
          {eyebrowText && <span style={eyebrowStyle}>{eyebrowText}</span>}
          <h2 style={{ ...sectionHeadingStyle, fontSize: 'clamp(2.2rem, 4vw, 3.2rem)', maxWidth: '500px', marginBottom: subheading ? '0.75rem' : '0' }}>{heading}</h2>
          {subheading && <p style={{ ...bodyTextStyle, fontSize: '1rem', maxWidth: '500px', marginBottom: 0, color: '#000' }}>{subheading}</p>}
        </div>
        <div style={{ display: 'flex', gap: '4rem', position: 'relative', flexDirection: reversed ? 'row-reverse' : 'row' }}>
          <div style={{ flex: '0 0 40%' }}>
            {features.map((f, i) => (
              <div key={i} ref={el => textRefs.current[i] = el} style={{
                minHeight: '80vh', display: 'flex', flexDirection: 'column', justifyContent: 'center',
                opacity: activeIndex === i ? 1 : 0.25, transition: 'opacity 0.4s ease',
              }}>
                <h3 style={{ fontFamily: inter, fontWeight: 700, fontSize: '2rem', letterSpacing: '-0.02em', color: '#000', marginBottom: '0.75rem' }}>{f.title}</h3>
                <p style={{ ...bodyTextStyle, fontSize: '1rem', maxWidth: '420px', color: '#000' }}>{f.desc}</p>
              </div>
            ))}
          </div>
          <div style={{ flex: '0 0 38%', minWidth: 0, marginLeft: reversed ? '0' : 'auto', marginRight: reversed ? 'auto' : '1rem' }}>
            <div style={{ position: 'sticky', top: '180px', height: 'clamp(500px, 72vh, 780px)', borderRadius: '8px', overflow: 'hidden', background: '#fff' }}>
              {features.map((f, i) => (
                f.img ? (
                  <div key={i} style={{
                    position: 'absolute', inset: 0,
                    background: f.bg || 'transparent',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    clipPath: (panelVisible && activeIndex === i) ? 'inset(0 0 0 0)' : reversed ? 'inset(0 100% 0 0)' : 'inset(0 0 0 100%)',
                    transition: 'clip-path 0.6s ease', zIndex: activeIndex === i ? 2 : 1,
                  }}>
                    <img src={f.img} alt={f.title} style={{
                      width: f.fit === 'contain' ? '95%' : '100%', height: f.fit === 'contain' ? '95%' : '100%',
                      objectFit: f.fit || 'cover', borderRadius: f.fit === 'contain' ? '4px' : 0, margin: 'auto',
                    }} loading="lazy" />
                  </div>
                ) : (
                  <video key={i} ref={el => videoRefs.current[i] = el} src={f.video} muted loop playsInline preload="metadata" onLoadedMetadata={e => { e.target.currentTime = 5; }} style={{
                    position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover',
                    clipPath: (panelVisible && activeIndex === i) ? 'inset(0 0 0 0)' : reversed ? 'inset(0 100% 0 0)' : 'inset(0 0 0 100%)',
                    transition: 'clip-path 0.6s ease', zIndex: activeIndex === i ? 2 : 1,
                  }} />
                )
              ))}
            </div>
          </div>
        </div>
      </Container>
    </div>
  );
}

function CountUp({ end, suffix = '', duration = 2000 }) {
  const ref = useRef(null);
  const [count, setCount] = useState(0);
  const [started, setStarted] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !started) {
        setStarted(true);
        observer.disconnect();
      }
    }, { threshold: 0.3 });
    observer.observe(el);
    return () => observer.disconnect();
  }, [started]);

  useEffect(() => {
    if (!started) return;
    const startTime = performance.now();
    const step = (now) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.round(eased * end));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [started, end, duration]);

  return <span ref={ref}>{count.toLocaleString()}{suffix && <span style={{ color: '#28a745' }}>{suffix}</span>}</span>;
}

function ProofStrip() {
  const stats = [
    { end: 13, suffix: '+', label: 'years at origin' },
    { end: 250, suffix: 'k+', label: 'farms mapped' },
    { end: 16, suffix: 'M+', label: 'alerts sent' },
    { end: 17, suffix: 'k+', label: 'ha monitored' },
  ];

  return (
    <section style={{ paddingTop: '4rem', paddingBottom: '4rem' }}>
      <Container>
        <Row className="text-center">
          {stats.map((item, i) => (
            <Col key={i} xs="6" md="3">
              <p style={{ fontFamily: "'Inter', sans-serif", fontWeight: 800, fontSize: 'clamp(2.2rem, 4vw, 4rem)', color: '#000', marginTop: 0, marginBottom: '0.25rem', letterSpacing: '-0.03em', lineHeight: 1 }}>
                <CountUp end={item.end} suffix={item.suffix} />
              </p>
              <p style={{ fontFamily: "'Inter', sans-serif", fontWeight: 400, fontSize: '0.95rem', color: '#000', margin: 0 }}>{item.label}</p>
            </Col>
          ))}
        </Row>
      </Container>
    </section>
  );
}

function Index() {
  const [lockedSection, setLockedSection] = useState(null);
  const [hoveredFeature, setHoveredFeature] = useState(0);
  const [selectedAudience, setSelectedAudience] = useState('farmers');
  const [imageOpacity, setImageOpacity] = useState(1);
  const leftColRef = useRef(null);

  React.useEffect(() => {
    const handleScroll = () => {
      if (!leftColRef.current) return;
      const rect = leftColRef.current.getBoundingClientRect();
      // Fade in as section enters viewport from bottom
      const fadeInRange = 200;
      const fadeInStart = window.innerHeight;
      const fadeInEnd = window.innerHeight - fadeInRange;
      // Fade out as section leaves viewport at bottom
      const distanceFromUnstick = rect.bottom - window.innerHeight;
      const fadeDelay = 450;
      const fadeRange = 50;
      let opacity = 1;
      if (rect.top > fadeInStart) {
        opacity = 0;
      } else if (rect.top > fadeInEnd) {
        opacity = 1 - (rect.top - fadeInEnd) / fadeInRange;
      } else if (distanceFromUnstick < -fadeDelay) {
        const pastDelay = Math.abs(distanceFromUnstick) - fadeDelay;
        opacity = Math.max(0, 1 - pastDelay / fadeRange);
      }
      setImageOpacity(opacity);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSectionClick = (idx) => {
    setLockedSection(prev => prev === idx ? null : idx);
  };

  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Weathersafe',
    url: 'https://www.weathersafe.co.uk',
    logo: 'https://www.weathersafe.co.uk/images/ws-icon-green.webp',
    description: 'Origin intelligence platform for coffee supply chains. Combines satellite monitoring, AI, weather forecasting and SMS farmer advisory with GAP77 regenerative farming and EUDR compliance reporting.',
    foundingDate: '2013',
    founder: { '@type': 'Person', name: 'David Mills' },
    areaServed: ['Kenya', 'Rwanda', 'East Africa'],
    knowsAbout: [
      'EUDR compliance',
      'coffee supply chain traceability',
      'satellite monitoring of coffee farms',
      'deforestation-free coffee sourcing',
      'GAP77 regenerative farming',
      'coffee farm mapping',
      'coffee yield forecasting',
      'SMS weather alerts for smallholder farmers',
      'washing station operations',
      'origin intelligence',
    ],
    funder: [
      { '@type': 'Organization', name: 'European Space Agency', url: 'https://www.esa.int/' },
      { '@type': 'Organization', name: 'UK Space Agency', url: 'https://www.gov.uk/government/organisations/uk-space-agency' },
      { '@type': 'Organization', name: 'Innovate UK', url: 'https://www.ukri.org/councils/innovate-uk/' },
      { '@type': 'Organization', name: 'Satellite Applications Catapult', url: 'https://sa.catapult.org.uk/' },
    ],
  };

  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'Weathersafe',
    url: 'https://www.weathersafe.co.uk',
    description: 'Coffee origin intelligence and EUDR compliance — satellite, AI, weather and SMS for coffee supply chains.',
    publisher: { '@type': 'Organization', name: 'Weathersafe' },
    inLanguage: 'en-GB',
  };

  const softwareApplicationSchema = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'Weathersafe Origin Intelligence Platform',
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Web',
    url: 'https://www.weathersafe.co.uk',
    description: 'Coffee origin intelligence platform combining satellite farm health monitoring, hyper-local weather forecasting, condition-triggered SMS alerts, and the GAP77 regenerative farming standard. Delivered through coffee washing stations to support EUDR Due Diligence Statements, SBTi FLAG-validated Scope 3 reporting, and CSRD disclosures.',
    provider: { '@type': 'Organization', name: 'Weathersafe' },
    audience: {
      '@type': 'BusinessAudience',
      audienceType: 'Coffee roasters, traders, importers, washing station operators',
    },
    featureList: [
      'Farm-level GPS mapping',
      'Satellite health monitoring at 1.5-metre resolution',
      'Hyper-local WRF weather forecasting',
      'Condition-triggered SMS farmer alerts',
      'GAP77 regenerative farming standard',
      'EUDR Due Diligence Statement evidence',
      'SBTi FLAG Scope 3 farm-level activity data',
      'CSRD-compatible practice records',
      'Origin Enviro+ Report deliverable',
      'Coffee yield forecasts calibrated against 13 years of ground-truth data',
    ],
  };

  return (
    <AppLayout
      title="EUDR Coffee Compliance & Origin Intelligence Platform"
      description="Build traceability, plan procurement, prove impact. Satellite + GAP77 + SMS for coffee supply chains. EUDR & SBTi FLAG ready. 250,000+ farms mapped."
      jsonLd={[organizationSchema, websiteSchema, softwareApplicationSchema]}
    >

      {/*  Hero Banner Section  */}
      <HeroBanner />
      {/*  Hero Banner Section End  */}

      {/* Scroll Journey Title */}
      <NioSection className="pb-0" style={{ marginBottom: '-2rem', paddingTop: 'clamp(4rem, 6vw, 9rem)' }}>
        <div className="nl-section-head text-center pb-0">
          <h2 className="origin-title">The origin intelligence platform<br/>for high-performing coffee brands.</h2>
        </div>
      </NioSection>

      {/* Environmental Management Section Start */}
      <NioSection className="py-10 py-lg-90" fluid style={{ marginTop: '4rem', paddingLeft: 'clamp(4.75rem, 12.5vw, 24.5rem)', paddingRight: 'clamp(4.75rem, 12.5vw, 24.5rem)' }}>
        <NioSection.Content>
          <Row className="gy-5 gy-lg-0">
            <Col md="6" lg="4">
              <NioCard className="bg-transparent border-0 h-100">
                <div className="card-img-top border rounded-2 overflow-hidden" style={{ height: 'clamp(272px, 34vw, 532px)', position: 'relative', background: 'linear-gradient(135deg, #f5f5f5 0%, #e8f5e9 100%)' }}>
                  {/* Fan animation */}
                  <div style={{
                    position: 'absolute',
                    left: '0%',
                    top: '-250%',
                    bottom: '-250%',
                    aspectRatio: '1 / 1',
                    borderRadius: '50%',
                    overflow: 'hidden',
                    pointerEvents: 'none',
                    zIndex: 0,
                  }}>
                    <div style={{
                      position: 'absolute',
                      inset: 0,
                      transformOrigin: '62% 50%',
                      background: 'conic-gradient(from 0deg at 62% 50%, transparent 0deg, rgba(40,167,69,0.12) 15deg, transparent 30deg, rgba(40,167,69,0.1) 55deg, transparent 70deg, rgba(40,167,69,0.12) 90deg, transparent 105deg, rgba(40,167,69,0.14) 125deg, transparent 140deg, rgba(40,167,69,0.12) 160deg, transparent 175deg, rgba(40,167,69,0.14) 195deg, transparent 210deg, rgba(40,167,69,0.12) 230deg, transparent 245deg, rgba(40,167,69,0.1) 265deg, transparent 280deg, rgba(40,167,69,0.12) 305deg, transparent 320deg, rgba(40,167,69,0.1) 345deg, transparent 360deg)',
                      animation: 'fanRotate 18s linear infinite',
                    }} />
                  </div>
                  {/* Laptop with map — oversized, showing the left corner of the screen */}
                  <div style={{ position: 'absolute', top: 'calc(-20% + 8rem)', left: 'calc(-10% + 1rem)', width: '300%', zIndex: 1 }}>
                    <img src="images/laptop-mockup.webp" alt="Weathersafe origin intelligence platform showing farm map and GAP77 dashboard" width="908" height="593" style={{ width: '100%', height: 'auto', display: 'block', position: 'relative', zIndex: 2 }} loading="lazy" />
                    {/* Screen area with map + chrome */}
                    <div style={{
                      position: 'absolute',
                      top: 'calc(11.8% - 0.3rem)', left: 'calc(15.2% - 2.7rem)',
                      width: '69.6%', height: '67%',
                      zIndex: 1,
                      overflow: 'hidden',
                    }}>
                      <img src="images/blog/blogfield.webp" alt="Farm map" width="1245" height="465" style={{
                        position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', zIndex: 0,
                      }} loading="lazy" />
                      {/* Top bar */}
                      <div style={{
                        position: 'absolute', top: 0, left: 0, right: 0, height: '6%',
                        background: 'rgba(20,20,20,0.85)', display: 'flex', alignItems: 'center', padding: '0 0.75rem', gap: '0.5rem', zIndex: 3,
                      }}>
                        <div style={{ display: 'flex', gap: '0.2rem' }}>
                          <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#ff5f57' }} />
                          <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#febc2e' }} />
                          <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#28c840' }} />
                        </div>
                        <span style={{ fontFamily: "'Inter', sans-serif", fontSize: '0.55rem', fontWeight: 600, color: '#fff', opacity: 0.9 }}>Farmer Database</span>
                        <span style={{ fontFamily: "'Inter', sans-serif", fontSize: '0.55rem', fontWeight: 500, color: '#fff', opacity: 0.5 }}>Farmer Survey</span>
                      </div>
                      {/* Left icon bar */}
                      <div style={{
                        position: 'absolute', top: '6%', left: 0, bottom: '8%', width: '5%',
                        background: 'rgba(20,20,20,0.85)', display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '0.5rem 0', gap: '0.5rem', zIndex: 3,
                      }}>
                        {[0,1,2,3,4].map(i => (
                          <div key={i} style={{ width: '10px', height: '10px', borderRadius: '2px', background: 'rgba(255,255,255,0.25)' }} />
                        ))}
                      </div>
                      {/* Bottom breadcrumb */}
                      <div style={{
                        position: 'absolute', bottom: 0, left: 0, right: 0, height: '8%',
                        background: 'rgba(20,20,20,0.85)', display: 'flex', alignItems: 'center', padding: '0 0.75rem', gap: '0.35rem', zIndex: 3,
                      }}>
                        <span style={{ fontFamily: "'Inter', sans-serif", fontSize: '0.5rem', fontWeight: 500, color: 'rgba(255,255,255,0.5)' }}>□ Origin</span>
                        <span style={{ fontFamily: "'Inter', sans-serif", fontSize: '0.5rem', color: 'rgba(255,255,255,0.3)' }}>›</span>
                        <span style={{ fontFamily: "'Inter', sans-serif", fontSize: '0.5rem', fontWeight: 500, color: 'rgba(255,255,255,0.5)' }}>□ Farm Maps</span>
                        <span style={{ fontFamily: "'Inter', sans-serif", fontSize: '0.5rem', color: 'rgba(255,255,255,0.3)' }}>›</span>
                        <span style={{ fontFamily: "'Inter', sans-serif", fontSize: '0.5rem', fontWeight: 600, color: '#28a745' }}>□ GAP77</span>
                      </div>
                    </div>
                  </div>
                </div>
                <NioCard.Body className="px-0 pt-4">
                  <h3 className="h6">Build Traceability</h3>
                  <p className="fs-8 mb-0" style={{ color: '#000' }}>Every farm GPS-mapped. Every lot linked. Farm-level visibility across your origins.</p>
                  <a href="/farm-maps" className="landing-card-link mt-3 d-inline-flex align-items-center" style={{ color: '#000' }}>
                    <span className="landing-card-link__text">Explore traceability</span>
                    <span className="landing-card-link__arrow">&#8594;</span>
                  </a>
                </NioCard.Body>
              </NioCard>
            </Col>
            <Col md="6" lg="4">
              <NioCard className="bg-transparent border-0 h-100">
                <div className="card-img-top border rounded-2 bg-gray-400 overflow-hidden" style={{ height: 'clamp(272px, 34vw, 532px)' }}>
                  <img src="images/farmer-training.webp" alt="Farmer training session at a coffee washing station" width="913" height="609" style={{ objectFit: 'cover', width: '100%', height: '100%' }} loading="lazy" />
                </div>
                <NioCard.Body className="px-0 pt-4">
                  <h3 className="h6">Manage Standards</h3>
                  <p className="fs-8 mb-0" style={{ color: '#000' }}>77 farming practices delivered through your washing stations. Training, alerts, and quality — managed every season.</p>
                  <a href="/about-gap-77" className="landing-card-link mt-3 d-inline-flex align-items-center" style={{ color: '#000' }}>
                    <span className="landing-card-link__text">Start managing</span>
                    <span className="landing-card-link__arrow">&#8594;</span>
                  </a>
                </NioCard.Body>
              </NioCard>
            </Col>
            <Col md="6" lg="4">
              <NioCard className="bg-transparent border-0 h-100">
                <div className="card-img-top border rounded-2 overflow-hidden" style={{ height: 'clamp(272px, 34vw, 532px)', position: 'relative', background: 'linear-gradient(135deg, #e8e8e8 0%, #d4ead9 100%)' }}>
                  {/* Fan animation */}
                  <div style={{
                    position: 'absolute',
                    left: '0%',
                    top: '-250%',
                    bottom: '-250%',
                    aspectRatio: '1 / 1',
                    borderRadius: '50%',
                    overflow: 'hidden',
                    pointerEvents: 'none',
                    zIndex: 0,
                  }}>
                    <div style={{
                      position: 'absolute',
                      inset: 0,
                      transformOrigin: '62% 50%',
                      background: 'conic-gradient(from 0deg at 62% 50%, transparent 0deg, rgba(40,167,69,0.12) 15deg, transparent 30deg, rgba(40,167,69,0.1) 55deg, transparent 70deg, rgba(40,167,69,0.12) 90deg, transparent 105deg, rgba(40,167,69,0.14) 125deg, transparent 140deg, rgba(40,167,69,0.12) 160deg, transparent 175deg, rgba(40,167,69,0.14) 195deg, transparent 210deg, rgba(40,167,69,0.12) 230deg, transparent 245deg, rgba(40,167,69,0.1) 265deg, transparent 280deg, rgba(40,167,69,0.12) 305deg, transparent 320deg, rgba(40,167,69,0.1) 345deg, transparent 360deg)',
                      animation: 'fanRotate 25s linear infinite reverse',
                    }} />
                  </div>
                  <img src="images/group-health-map.webp" alt="Farm health map dashboard showing coffee farm satellite monitoring" width="696" height="663" style={{ objectFit: 'cover', width: '100%', height: '100%', objectPosition: 'calc(50% + 2.2rem) calc(50% + 4rem)', position: 'relative', zIndex: 1 }} loading="lazy" />
                </div>
                <NioCard.Body className="px-0 pt-4">
                  <h3 className="h6">Prove Impact</h3>
                  <p className="fs-8 mb-0" style={{ color: '#000' }}>Verified farming practices. Farm health scores. Measurable outcomes across your network — evidence, not claims.</p>
                  <a href="/farm-health-scores" className="landing-card-link mt-3 d-inline-flex align-items-center" style={{ color: '#000' }}>
                    <span className="landing-card-link__text">See the evidence</span>
                    <span className="landing-card-link__arrow">&#8594;</span>
                  </a>
                </NioCard.Body>
              </NioCard>
            </Col>
          </Row>
        </NioSection.Content>
      </NioSection>
      {/* Environmental Management Section End */}

      {/*  Weather Precision Section Start  */}
      <NioSection className="pb-7 pb-lg-90" style={{ marginTop: '0rem' }}>
        <NioSection.Content>
          <Row>
            <Col lg="8">
              <div className="nl-section-haed pb-0" style={{ marginLeft: 'clamp(4rem, 9vw, 14rem)' }}>
                <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 'clamp(1.05rem, 2vw, 1.7rem)', fontWeight: 400, lineHeight: 1.5, color: '#000' }}>GAP77 is a farming standard — 77 practices developed over 13 years in real farms. Delivered through washing stations. Not certified — verified.</p>
                <a href="/how-it-works" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', marginTop: '1.5rem', padding: '0.75rem 1.75rem', background: '#28a745', color: '#fff', borderRadius: '4px', fontFamily: "'Inter', sans-serif", fontWeight: 600, fontSize: '0.95rem', textDecoration: 'none', letterSpacing: '-0.01em' }}>
                  Explore Origin Intelligence <span style={{ position: 'relative', top: '-2px' }}>&#8599;</span>
                </a>
              </div>
            </Col>
          </Row>
        </NioSection.Content>
      </NioSection>
      {/*  Weather Precision Section End  */}

      {/* Proof strip — animated count-up */}
      <ProofStrip />

      {/* Core capabilities — sticky scroll */}
      <section style={{ paddingTop: '4rem', paddingBottom: '6rem' }}>
        <StickyVideoSection
          heading="A technology-enforced standard."
          subheading="Weather forecasting, satellite data, AI, and 13 years of field experience — combined into a transition pathway your buyers can audit."
          features={[
            { title: 'Full traceability', desc: 'Farm-level visibility across every origin you source from. The proof your buyers and partners are already asking for.', img: 'images/help-center/mapfarmimage.webp' },
            { title: 'EUDR compliance', desc: 'Farm-level geolocation data and deforestation-free proof across your supply chain. Built from verified farm records.', img: 'images/help-center/jonas plot.png' },
            { title: 'Origin monitoring', desc: 'Continuous visibility across your farm network. Satellite health, weather, and pest alerts — not annual audits.', img: 'images/help-center/healthchange-portrait.webp' },
            { title: 'Supply forecasting', desc: 'Forward visibility on production across your origins. Yield forecasts per farm and zone.', img: 'images/help-center/cwsyeild-portrait.webp', fit: 'contain' },
          ]}
        />
      </section>

      {/* Laptop map banner */}
      <section style={{ paddingTop: '4rem', paddingBottom: '4rem' }}>
        <Container>
          <div className="overflow-hidden" style={{
            width: '100%', height: 'clamp(240px, 30vw, 480px)', borderRadius: '8px', position: 'relative',
            background: 'linear-gradient(135deg, #f5f5f5 0%, #e8f5e9 100%)',
            display: 'flex', alignItems: 'center', padding: '0 clamp(2rem, 4vw, 5rem)',
          }}>
            {/* Fan animation */}
            <div style={{
              position: 'absolute',
              left: '0%',
              top: '-250%',
              bottom: '-250%',
              aspectRatio: '1 / 1',
              borderRadius: '50%',
              overflow: 'hidden',
              pointerEvents: 'none',
              zIndex: 1,
            }}>
              <div style={{
                position: 'absolute',
                inset: 0,
                transformOrigin: '62% 50%',
                background: 'conic-gradient(from 0deg at 62% 50%, transparent 0deg, rgba(40,167,69,0.12) 15deg, transparent 30deg, rgba(40,167,69,0.1) 55deg, transparent 70deg, rgba(40,167,69,0.12) 90deg, transparent 105deg, rgba(40,167,69,0.14) 125deg, transparent 140deg, rgba(40,167,69,0.12) 160deg, transparent 175deg, rgba(40,167,69,0.14) 195deg, transparent 210deg, rgba(40,167,69,0.12) 230deg, transparent 245deg, rgba(40,167,69,0.1) 265deg, transparent 280deg, rgba(40,167,69,0.12) 305deg, transparent 320deg, rgba(40,167,69,0.1) 345deg, transparent 360deg)',
                animation: 'fanRotate 18s linear infinite',
              }} />
            </div>
            <div style={{ flex: '0 0 40%', zIndex: 2 }}>
              <h3 style={{ fontFamily: "'Inter', sans-serif", fontWeight: 800, fontSize: 'clamp(1.6rem, 3vw, 2.8rem)', color: '#000', lineHeight: 1.15, marginBottom: '1rem' }}>
                Map your origin.
              </h3>
              <p style={{ fontFamily: "'Inter', sans-serif", fontWeight: 400, fontSize: 'clamp(0.85rem, 1.2vw, 1.1rem)', color: '#555', lineHeight: 1.6, maxWidth: '360px', marginBottom: '1.5rem' }}>
                Every farm GPS-mapped with tree counts, plot boundaries, and farmer database.
              </p>
              <a href="/farm-maps" style={{ fontFamily: "'Inter', sans-serif", fontWeight: 600, fontSize: '0.9rem', color: '#fff', background: '#28a745', borderRadius: '6px', padding: '0.65rem 1.5rem', textDecoration: 'none', display: 'inline-block' }}>
                Explore mapping &#8594;
              </a>
            </div>
            {/* Laptop with map — oversized, cropped to bottom-right corner */}
            <div style={{ position: 'absolute', bottom: 'calc(-40% - 2rem)', right: '-20%', width: 'clamp(600px, 75vw, 1100px)', zIndex: 1 }}>
              <img src="images/laptop-mockup.webp" alt="" width="908" height="593" style={{ width: '100%', height: 'auto', display: 'block', position: 'relative', zIndex: 2 }} loading="lazy" />
              <img src="images/blog/blogfield.webp" alt="Weathersafe farm-mapping interface showing GPS-tagged coffee farm boundaries" width="1245" height="465" style={{
                position: 'absolute',
                top: '11.8%', left: 'calc(15.2% - 2rem)',
                width: '69.6%', height: '67%',
                objectFit: 'cover', zIndex: 1,
              }} loading="lazy" />
            </div>
          </div>
        </Container>
      </section>

      {/* Everything people love title + all feature menus */}
      <NioSection fluid style={{ paddingLeft: 'clamp(5rem, 12vw, 24rem)', paddingRight: 'clamp(1rem, 6vw, 15rem)', marginTop: '3rem' }}>
        <NioSection.Content>
          <h2 style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: 'clamp(1.8rem, 4vw, 3.4rem)', color: '#000', lineHeight: 1.2, letterSpacing: '-0.02em', maxWidth: '820px' }}>
            Everything <AudienceDropdown selected={selectedAudience} setSelected={(val) => { setSelectedAudience(val); setHoveredFeature(0); }} /><br />love about Weathersafe
          </h2>
          <Row style={{ marginTop: '4rem' }} className="align-items-start">
            {/* Left: all 3 menus stacked */}
            <Col lg="5" ref={leftColRef} style={{ paddingBottom: '20rem' }}>
              <div key={selectedAudience} className="audience-menus-fade">
              {audienceData[selectedAudience].menus.map((menu, mi) => (
                <div key={mi} style={{ marginTop: mi === 0 ? 0 : mi === 2 ? '12rem' : '16rem' }}>
                  <Row>
                    <Col lg="4">
                      <p style={{ fontFamily: "'Inter', sans-serif", fontWeight: 400, fontSize: '1.2rem', color: '#000', lineHeight: 1.5 }}>{menu.title}</p>
                    </Col>
                    <Col lg="8">
                      <p style={{ fontFamily: "'Inter', sans-serif", fontWeight: 400, fontSize: '1.2rem', color: '#000', lineHeight: 1.5 }}>{menu.desc}</p>
                      <div style={{ marginTop: '3rem' }}>
                        {menu.features.map((item, i) => {
                          const featureText = typeof item === 'string' ? item : item.text;
                          const featureHref = typeof item === 'string' ? null : item.href;
                          const sharedProps = {
                            key: i,
                            style: { textDecoration: 'none', cursor: 'default', color: 'inherit', display: 'block' },
                            onMouseEnter: e => {
                              setHoveredFeature(menu.offset + i);
                              e.currentTarget.querySelector('div').style.borderBottomColor = '#28a745';
                              e.currentTarget.querySelectorAll('span').forEach(s => s.style.color = '#28a745');
                              e.currentTarget.querySelector('.feat-arrow').style.transform = 'translateX(5px)';
                            },
                            onMouseLeave: e => {
                              e.currentTarget.querySelector('div').style.borderBottomColor = '#d0d0d0';
                              e.currentTarget.querySelectorAll('span').forEach(s => s.style.color = '#000');
                              e.currentTarget.querySelector('.feat-arrow').style.transform = 'translateX(0)';
                            },
                          };
                          const inner = (
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem 0', borderBottom: '1px solid #d0d0d0', transition: 'border-color 0.2s ease' }}>
                              <span style={{ fontFamily: "'Inter', sans-serif", fontWeight: 400, fontSize: '1rem', color: '#000', transition: 'color 0.2s ease' }}>{featureText}</span>
                              <span className="feat-arrow" style={{ color: '#000', fontSize: '1rem', transition: 'color 0.2s ease, transform 0.2s ease', display: 'inline-block' }}>&#8594;</span>
                            </div>
                          );
                          return featureHref
                            ? <Link to={featureHref} {...sharedProps}>{inner}</Link>
                            : <div {...sharedProps}>{inner}</div>;
                        })}
                      </div>
                    </Col>
                  </Row>
                </div>
              ))}
              </div>
            </Col>
            {/* Right: sticky image */}
            <Col className="ms-auto" style={{ position: 'sticky', top: '10vh', alignSelf: 'flex-start', marginRight: 'clamp(0rem, 3vw, 6rem)', flex: '0 0 37.5%', width: '37.5%' }}>
              <div className="border rounded-2 overflow-hidden" style={{ height: 'clamp(360px, 42vw, 640px)', position: 'relative', marginTop: '6px', opacity: imageOpacity, transition: 'opacity 0.1s linear' }}>
                {audienceData[selectedAudience].images.map((src, i) => (
                  <img key={i} src={src} alt="feature" style={{ objectFit: 'cover', width: '100%', height: '100%', position: 'absolute', inset: 0, opacity: hoveredFeature === i ? 1 : 0, transition: 'opacity 0.5s ease' }} loading="lazy" />
                ))}
              </div>
            </Col>
          </Row>
        </NioSection.Content>
      </NioSection>

      {/* CTA Split */}
      <NioSection className="py-10 py-lg-90" style={{ borderTop: '1px solid #f0f0f0', borderBottom: '1px solid #f0f0f0', paddingTop: '7rem', paddingBottom: '7rem' }}>
        <NioSection.Content>
          <Row className="align-items-start">
            <Col lg="5" style={{ marginTop: '-0.75rem' }}>
              <h2 style={{ fontFamily: "'Inter', sans-serif", fontWeight: 800, fontSize: 'clamp(2.2rem, 4vw, 3.5rem)', letterSpacing: '-0.03em', lineHeight: 1.05, color: '#000', marginBottom: '2rem' }}>
                Ready to see it<br />in action?
              </h2>
              <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                <a
                  href="/contact-us-solution"
                  style={{ fontFamily: "'Inter', sans-serif", fontWeight: 600, fontSize: '0.95rem', color: '#fff', background: '#28a745', borderRadius: '6px', padding: '0.75rem 1.75rem', textDecoration: 'none' }}
                >
                  Book a demo
                </a>
                <a
                  href="/contact-us-solution"
                  style={{ fontFamily: "'Inter', sans-serif", fontWeight: 600, fontSize: '0.95rem', color: '#000', background: '#fff', border: '1px solid #d0d0d0', borderRadius: '6px', padding: '0.75rem 1.75rem', textDecoration: 'none' }}
                >
                  Talk to our team
                </a>
              </div>
            </Col>
            <Col lg="5" className="offset-lg-2">
              <p style={{ fontFamily: "'Inter', sans-serif", fontWeight: 400, fontSize: 'clamp(1.1rem, 2vw, 1.55rem)', lineHeight: 1.4, color: '#333', marginBottom: 0 }}>
                Traceability, monitoring, and forecasting in a single platform. No data silos. No gaps in your supply chain.
              </p>
            </Col>
          </Row>
        </NioSection.Content>
      </NioSection>

    </AppLayout >
  )
}

export default Index;
